import { useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";

// Injects TikTok's official embed.js once and renders a creator card for @lac.ltd.
export default function TikTokFeed() {
  const mountRef = useRef(false);

  useEffect(() => {
    if (mountRef.current) return;
    mountRef.current = true;

    const existing = document.querySelector('script[src="https://www.tiktok.com/embed.js"]');
    if (existing) {
      // Re-trigger embed render if script already on page
      if (window.tiktokEmbed && typeof window.tiktokEmbed.lib?.render === "function") {
        window.tiktokEmbed.lib.render(document.querySelectorAll(".tiktok-embed"));
      }
      return;
    }
    const s = document.createElement("script");
    s.src = "https://www.tiktok.com/embed.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <section id="gallery" className="py-32 border-y border-white/5 bg-[#080808]" data-testid="tiktok-feed-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-4">
          <div className="text-xs tracking-[0.35em] text-[#D946EF] mb-4">/ 03 — RECENT WORK</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
            The latest from<br />
            <span className="neon-violet">@lac.ltd</span><br />— live.
          </h2>
          <p className="mt-6 text-zinc-400 font-body leading-relaxed max-w-sm">
            This feed pulls our most recent installs straight from TikTok — every car, every fibre,
            every finish. Real cars, real customers, no staged shots.
          </p>
          <div className="mt-8 flex flex-col gap-3">
            <a
              data-testid="tiktok-profile-cta"
              href="https://www.tiktok.com/@lac.ltd"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black font-semibold text-sm hover:bg-zinc-200 transition w-max"
            >
              Follow @lac.ltd on TikTok <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="https://www.instagram.com/lac.ltd"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white hover:border-[#FF2D87]/60 hover:text-[#FF2D87] transition w-max"
            >
              See @lac.ltd on Instagram
            </a>
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="relative rounded-sm border border-white/10 bg-[#050505] p-4 sm:p-6 overflow-hidden" style={{ minHeight: 640 }}>
            <blockquote
              className="tiktok-embed"
              cite="https://www.tiktok.com/@lac.ltd"
              data-unique-id="lac.ltd"
              data-embed-type="creator"
              style={{ maxWidth: 780, minWidth: 288, margin: "0 auto" }}
            >
              <section>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.tiktok.com/@lac.ltd?refer=creator_embed"
                  style={{ color: "#fff" }}
                >
                  @lac.ltd
                </a>
              </section>
            </blockquote>

            {/* Fallback card if the script fails to load / is blocked */}
            <noscript>
              <div className="text-center py-16">
                <a href="https://www.tiktok.com/@lac.ltd" className="text-[#D946EF] underline">
                  View @lac.ltd on TikTok →
                </a>
              </div>
            </noscript>
          </div>
        </div>
      </div>
    </section>
  );
}
